import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Spinner from "@/common/components/Spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronsRight } from "lucide-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { BranchInformation } from "../../components/form/BranchInformation";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import { GeneralInformation } from "../../components/form/GeneralInformation";
import { MasterClientUser } from "../../components/form/MasterClientUser";
import { CreditConfiguration } from "../../components/form/CreditConfiguration";
import { ConsumptionRestriction } from "../../components/form/ConsumptionRestriction";
import { PeriodRestriction } from "../../components/form/PeriodRestriction";
import { companyBranchSchema, CompanyBranchValidationSchema } from "../../schemas/companyBranch.schema";
import { companyBranchDefaultPeriodRestrictionData } from "@/features/equipments/utils/companyBranchDefaultPeriodRestrictionData";
import useCreateCompanyBranch from "../../hooks/useCreateCompanyBranch";
import { useEffect, useState } from "react";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { CompanyBranchesTransformer } from "../../services/companyBranchesTransformer";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import GoBackButton from "@/common/components/GoBackButton";

const CompanyBranchesCreate = () => {
  const navigate = useNavigate();
  const { filterParams, setFilterParams } = useFilterParams();

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [file, setFile] = useState<File>();

  const { data: companyData } = useMineCompany();

  const { mutate: createCompanyBranch, isPending } = useCreateCompanyBranch({
    onSuccess: () => {
      navigate(`/company-branches`);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
        transformer: CompanyBranchesTransformer.errorsTransformer(),
      })(error);
    },
  });

  const formMethods = useForm<CompanyBranchValidationSchema>({
    resolver: zodResolver(companyBranchSchema),
    defaultValues: {
      period_restrictions_attributes: companyBranchDefaultPeriodRestrictionData(),
    }
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    getValues,
    formState: { errors }
  } = formMethods;

  const onSubmit: SubmitHandler<CompanyBranchValidationSchema> = (data) => {
    createCompanyBranch({ form: data, file });
  };

  useEffect(() => {
    if (filterParams?.parent?.value) {
      setValue('parent', filterParams.parent);
      setFilterParams(() => ({}))
    }
  }, [filterParams?.parent?.value]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/company-branches" searchLastRouteFromFallback />

            <p className="text-white-400 font-normal">
              Estrutura
            </p>

            <ChevronsRight color='#ffffffb3' />

            <h2>
              Cadastrar filial
            </h2>
          </div>

          <div className="flex gap-4">
            <Link to='/company-branches'>
              <Button variant='outline-error'>
                Cancelar
              </Button>
            </Link>

            <Button name="submit" type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" >
              {isPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Cadastrar Filial'}
            </Button>
          </div>
        </div>

        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
          <p className="text-lg font-bold">
            Dados da filial
          </p>

          <BranchInformation
            register={register}
            errors={errors}
            control={control}
            showParentIdField
          />

          <GeneralInformation
            reset={reset}
            getValues={getValues}
            companyData={companyData}
            register={register}
            errors={errors}
            control={control}
            watch={watch}
            setValue={setValue}
            setError={setError}
            file={file}
            setFile={setFile}
            disableCnpj={false}
            clearErrors={clearErrors}
          />

          <MasterClientUser
            errors={errors}
            control={control}
          />

          <CreditConfiguration
            companyData={companyData}
            register={register}
            control={control}
            watch={watch}
            errors={errors}
          />

          <ConsumptionRestriction disabled={isPending} />

          <PeriodRestriction
            control={control}
            register={register}
            watch={watch}
            errors={errors}
            disabled={isPending}
          />
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError
        />
      )}
    </FormProvider>


  );
};

export default CompanyBranchesCreate;
